import React, { useEffect, useState } from 'react';
import {
  Accordion,
  Space,
  Text,
  Conditional,
  Timeline,
  InputField,
  Dropdown,
  Icon
} from '@cashfree-intl/coherent';
import CustomIcons from 'components/CustomIcons';
import {
  StyledCheckbox,
  StyledChipWrapper,
  StyledHorizontalLine,
  StyledInputField,
  StyledInputWithCounter,
  StyledPaper,
  StyledRadioGroup,
  StyledRetriesWrapper,
  StyledRetryMessage,
  StyledSpace,
} from '../styled';
import {
  CUSTOM,
  DEFAULT,
  EXPONENTIAL,
  POLICY_RADIO_OPTIONS,
  TIME_UNIT,
} from '../constants';
import CustomIcon from 'components/CustomIcons/main';
import { getBarPercentage, getDisableOption, getRetryMessage } from '../helpers';
import { PLACEHOLDER_TEXT } from 'utils/constants';
import AttemptBar from './AttempBar';

const Policy = ({
  handleAccordianItemClick,
  activeIndex,
  index,
  policyData,
  handlePolicyList,
  checkedItems,
  onSelectAllHandler,
  onClearAllHandler,
  filteredData,
  removeEvent,
  addEvent,
  handleFilterSearch,
  searchInput,
  policyListLength,
  handlePolicyListDelete,
  existingWebhooks,
  endpointDetails,
}) => {
  const [customIntervalValue, setCustomIntervalValue] = useState(policyData.retryInterval);
  const [versionConflictWarning, setVersionConflictWarning] = useState('');

  const handleOptionalRadioOptions = obj => (
    {
      ...obj,
      checked: policyData.retryType === obj.value,
    }
  )

  const handleChange = (obj, i) => {
    const val = parseInt(obj.value) || 0
    if (val > 2147483647) {
      return;
    }
    const tempInterval = customIntervalValue;
    tempInterval.splice(i, 1, val);
    setCustomIntervalValue([...tempInterval]);
    handlePolicyList({ name: 'retryInterval', value: tempInterval }, index);
  };

  useEffect(() => {
    if (policyData.retryType === CUSTOM) {
      setCustomIntervalValue([...policyData.retryInterval])
    }

  }, [policyData.maxRetryAttempts, policyData.retryType])

  const handleEventCheckboxChange = (item, isChecked) => {
    const currentEndpointUrl = endpointDetails.channelValue.trim().replace(/\/+$/, '');
    const currentCfApiVersion = endpointDetails.cfApiVersion;
    const notificationType = item;

    let conflictFound = false;
    let conflictingVersions = [];

    existingWebhooks.forEach(existingWebhook => {
      const existingWebhookUrl = existingWebhook.channelValue.trim().replace(/\/+$/, '');
      if (
        existingWebhookUrl === currentEndpointUrl &&
        existingWebhook.CfApiVersion !== currentCfApiVersion
      ) {
        const hasEvent =
          existingWebhook.merchantNotificationTypeDetailsResponse &&
          existingWebhook.merchantNotificationTypeDetailsResponse.some(
            event =>
              event.NotificationType.toLowerCase().replaceAll('_', ' ') ===
                notificationType && event.IsActive,
          );

        if (hasEvent) {
          conflictFound = true;
          if (!conflictingVersions.includes(existingWebhook.CfApiVersion)) {
            conflictingVersions.push(existingWebhook.CfApiVersion);
          }
        }
      }
    });

    if (isChecked && conflictFound) {
      const warningMessage = `Warning: This change will result in duplicate "${notificationType}" webhook notifications.\nMultiple versions (${conflictingVersions.join(
        ', ',
      )}) are currently enabled for this event. To avoid duplicate webhooks, make sure to disable any unused versions`;
      setVersionConflictWarning(warningMessage);
    } else {
      setVersionConflictWarning('');
    }
    if (isChecked) {
      addEvent(item, index);
    } else {
      removeEvent(item, index);
    }
  };

  return (
    <StyledPaper className="accordion-policy-timeline mt-2" key={index}>
      <Accordion>
        <Accordion.Title
          active={activeIndex === index}
          index={index}
          onClick={() => handleAccordianItemClick(index)}
        >
          <Space justifyContent="space-between">
            <Text variant="h16" strong>
              <span className='mr-2'>
                {activeIndex === index ? (
                  <Icon name="chevron-down" />
                ) : (
                  <Icon name="chevron-up" />
                )}
              </span>
              Policy {index + 1}
            </Text>
            {policyListLength > 1 && <CustomIcons componentName="DELETE_SPLIT" onClick={
              (e) => {
                e.stopPropagation()
                handlePolicyListDelete(index)
              }
            } />}
          </Space>
          <Conditional if={activeIndex !== index}>
            <Space gap={3} className="ml-5 mt-2">
              <Text color="bodyLight">Retry Policy :</Text>
              <Text strong> {policyData?.retryType}</Text>
            </Space>
            <StyledSpace gap={7} className="ml-5 mt-1">
              <Text color="bodyLight">Events :</Text>
              <Text strong > {checkedItems.length && checkedItems[index].join(',') || PLACEHOLDER_TEXT}</Text>
            </StyledSpace>
          </Conditional>
        </Accordion.Title>
        <Accordion.Content active={activeIndex === index}>
          <StyledHorizontalLine />
          <Timeline openItems={['0', '1']} striped className="policy-timeline">
            <Timeline.Item key={'0'} addedOn="">
              <Timeline.Title index={'0'} label="Add Policy Details" />
              <Timeline.Content panelId={'0'} className="pt-2">
                <StyledRadioGroup
                  className="radio-group mb-2"
                  fieldLabel={POLICY_RADIO_OPTIONS.label}
                  radioOptions={POLICY_RADIO_OPTIONS.radioOptions[index].map(
                    handleOptionalRadioOptions,
                  )}
                  onChange={(_e, obj) => {
                    handlePolicyList(obj, index);
                  }}
                />
                <Conditional
                  if={policyData.retryType !== DEFAULT}
                  className="mt-1"
                >
                  <StyledInputWithCounter
                    label="Enter no. of retries (Max allowed - 10)"
                    name="maxRetryAttempts"
                    value={policyData.maxRetryAttempts}
                    disableMinus={policyData.maxRetryAttempts <= 1}
                    disablePlus={policyData.maxRetryAttempts >= 10}
                    handleAddClick={() => {
                      handlePolicyList(
                        {
                          name: 'maxRetryAttempts',
                          value: policyData.maxRetryAttempts + 1,
                        },
                        index,
                      );
                    }}
                    handleSubtractClick={() => {
                      handlePolicyList(
                        {
                          name: 'maxRetryAttempts',
                          value: policyData.maxRetryAttempts - 1,
                        },
                        index,
                      );
                    }}
                    onChange={e => {
                      if (
                        parseInt(e.target.value, 10) > 10 ||
                        parseInt(e.target.value, 10) < 0
                      ) {
                        return;
                      }
                      handlePolicyList(
                        {
                          name: 'maxRetryAttempts',
                          value: parseInt(e.target.value, 10),
                        },
                        index,
                      );
                    }}
                  />
                  <StyledRetriesWrapper>
                    <Conditional if={policyData.retryType !== CUSTOM}>
                      <InputField
                        type='number'
                        name='intervalValue'
                        label="Enter Time Interval"
                        value={policyData.intervalValue}
                        onChange={e => {
                          if (e.target.value > 2147483647) {
                            return;
                          }
                          handlePolicyList(
                            {
                              name: 'intervalValue',
                              value: parseInt(e.target.value),
                            },
                            index,
                          );
                        }}
                      />
                    </Conditional>
                    <Conditional if={policyData.retryType === CUSTOM}>
                      <div>
                        <Text color="bodyLight" className="mb-1">
                          Enter Time Interval
                        </Text>
                        <StyledRetriesWrapper>
                          {customIntervalValue.map((data, i) => (
                            <StyledInputField
                              value={data}
                              onChange={(e, obj) => handleChange(obj, i)}
                            />
                          ))}
                        </StyledRetriesWrapper>
                      </div>
                    </Conditional>
                    <div>
                      <Text color="bodyLight" className="mb-1">
                        Select Time Unit
                      </Text>
                      <Dropdown
                        placeholder="Select the option"
                        selection
                        icon="chevron down"
                        name="timeUnit"
                        value={policyData.timeUnit}
                        options={TIME_UNIT}
                        onChange={(_e, obj) => {
                          handlePolicyList(
                            {
                              name: 'timeUnit',
                              value: obj.value,
                            },
                            index,
                          );
                        }}
                      />
                    </div>
                    <Conditional if={policyData.retryType === EXPONENTIAL}>
                      <div>
                        <StyledInputWithCounter
                          label="Multiplier"
                          name="multiplier"
                          value={policyData.multiplier}
                          disableMinus={policyData.multiplier <= 1}
                          disablePlus={policyData.multiplier >= 10}
                          handleAddClick={() => {
                            handlePolicyList(
                              {
                                name: 'multiplier',
                                value: policyData.multiplier + 1,
                              },
                              index,
                            );
                          }}
                          handleSubtractClick={() => {
                            handlePolicyList(
                              {
                                name: 'multiplier',
                                value: policyData.multiplier - 1,
                              },
                              index,
                            );
                          }}
                          onChange={e => {
                            handlePolicyList(
                              {
                                name: 'multiplier',
                                value: parseInt(e.target.value, 10),
                              },
                              index,
                            );
                          }}
                        />
                      </div>
                    </Conditional>
                  </StyledRetriesWrapper>
                </Conditional>
                <AttemptBar interval={getBarPercentage(policyData.retryType, policyData.retryInterval)} />
                <StyledRetryMessage variant='b14'>
                  {getRetryMessage(policyData)}
                </StyledRetryMessage>
              </Timeline.Content>
            </Timeline.Item>
            <Timeline.Item key={'1'} addedOn="">
              <Timeline.Title
                index={'1'}
                label={
                  <>
                    Select Events{' '}
                    <Conditional if={checkedItems.length && checkedItems[index].length}>
                      <span className="pl-1">
                        ({checkedItems.length ? checkedItems[index].length : <></>} selected)
                      </span>
                    </Conditional>
                  </>
                }
              >
                <Space>
                  <Text
                    className="selection-text"
                    color="primary"
                    variant="b12"
                    strong
                    onClick={e => {
                      e.stopPropagation();
                      onSelectAllHandler(index);
                    }}
                    data-event-name="Link_SelectAll"
                  >
                    Select all /
                  </Text>
                  <Text
                    className="selection-text"
                    color="primary"
                    variant="b12"
                    strong
                    onClick={e => {
                      e.stopPropagation();
                      onClearAllHandler(index);
                    }}
                    data-event-name="Link_ClearSelected"
                  >
                    {' '}
                    Clear selected
                  </Text>
                </Space>
              </Timeline.Title>
              <Timeline.Content panelId={'1'}>
                {versionConflictWarning && (
                  <Space className="alert-info-space py-1 mb-1">
                    <div>
                      <CustomIcons
                        width="18"
                        className="info"
                        componentName="ALERT_INFO"
                      />
                    </div>
                    <Text className="ml-2" variant="p14" color="warning">
                      <Text color="warning" strong>{versionConflictWarning.split('\n')[0]}</Text>
                      {versionConflictWarning.split('\n')[1]}
                    </Text>
                  </Space>
                )}
                <StyledChipWrapper className="mb-5">
                  {checkedItems.length ? checkedItems[index].map(item => (
                    <div key={item} className="events-chip px-1 mr-1 mb-1">
                      <span className="pr-1">{item}</span>
                      <span
                        className="pointer"
                        onClick={() => {
                          handleEventCheckboxChange(item, false);
                        }}
                        data-event-name="Form_AddTransactionWebhookModal_Step2_Icon_Close"
                        data-ignore-inner-text="true"
                      >
                        <CustomIcon componentName="CLOSE_SMALL" />
                      </span>
                    </div>
                  )) : <></>}
                </StyledChipWrapper>
                <InputField
                  name='search-input'
                  className="search-events-webhook"
                  value={searchInput[index]}
                  onChange={e => {
                    handleFilterSearch(e.target.value.toLowerCase(), index);
                  }}
                  fluid
                  iconPosition="left"
                  icon={<CustomIcon componentName="SEARCH" />}
                  placeholder="Search events you want to subscribe"
                  autoFocus
                />
                <div className="scroll-bar">
                  {(filteredData &&
                    filteredData[index].length) ?
                    filteredData[index].map(item => (
                      <Space alignItems='center' className='mb-2' key={item} onClick={() => {
                        handleEventCheckboxChange(
                          item,
                          !(checkedItems.length && checkedItems[index].includes(item))
                        );
                      }}>
                        <StyledCheckbox
                          name={item}
                          label={''}
                          checked={checkedItems.length && checkedItems[index].includes(item)}
                          disabled={getDisableOption(item, checkedItems, index)}
                          className='events-checkbox'
                        />
                        <Space direction="column">
                          {item}
                          <Conditional if={item === 'abandoned checkout'}>
                            <Text color="bodyLight" variant="b12">
                              This event is triggered when a user abandons their checkout.
                            </Text>
                          </Conditional>
                        </Space>
                      </Space>
                    )) : <></>}
                </div>
              </Timeline.Content>
            </Timeline.Item>
          </Timeline>
        </Accordion.Content>
      </Accordion>
    </StyledPaper>
  );
};

export default Policy;
